import { ApiError, type CameraMedia, queryKeys, type SupportTicketMedia } from '@mevaser/shared'
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Paperclip } from 'lucide-react'
import { useState } from 'react'
import { api, apiBaseUrl } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'
import { CheckoutErrorNote, MediaViewer, mediaDescription } from './camera-media-dialog.js'
import { MEDIA_KIND_ICON, MEDIA_KIND_LABEL, mediaTime } from './camera-media-grid.js'

/**
 * Snapshots, clips, tablet photos and screen captures that staff attached to this ticket
 * as evidence. They are viewed through the ticket, so support staff need no
 * kiosk permissions to see what was attached for them — and nothing else.
 */
export function TicketCameraMedia({ ticketId }: { ticketId: string }) {
  const { can } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [viewing, setViewing] = useState<CameraMedia | null>(null)

  const { data: attachments } = useQuery({
    queryKey: queryKeys.supportTickets.media(ticketId),
    queryFn: () => api.get<SupportTicketMedia[]>(`/support-tickets/${ticketId}/media`),
  })

  const detach = useMutation({
    mutationFn: (mediaId: string) => api.delete(`/support-tickets/${ticketId}/media/${mediaId}`),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.supportTickets.media(ticketId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.cameras.all() }),
      ])
      toast.success('Detached from the ticket')
      setViewing(null)
    },
    onError: (err) =>
      toast.error('Could not detach it', err instanceof ApiError ? err.message : undefined),
  })

  const canBrowse = can('kiosks:read')
  if (!attachments?.length && !canBrowse) return null

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Paperclip className="size-4" /> Camera media
        </CardTitle>
      </CardHeader>
      <CardContent>
        {attachments?.length ? (
          <ul className="space-y-1">
            {attachments.map(({ id, media }) => {
              const Icon = MEDIA_KIND_ICON[media.kind]
              return (
                <li key={id}>
                  <button
                    type="button"
                    onClick={() => setViewing(media)}
                    className="flex w-full items-center gap-2 rounded-md p-2 text-left text-sm hover:bg-surface-sunken"
                  >
                    <Icon className="size-4 shrink-0 text-foreground-muted" aria-hidden="true" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">
                        {media.machineName} · {media.cameraName}
                      </span>
                      <span className="block text-2xs text-foreground-muted">
                        {MEDIA_KIND_LABEL[media.kind]} · {mediaTime(media)}
                      </span>
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        ) : (
          <p className="text-sm text-foreground-muted">
            Nothing attached. Open a snapshot or clip on the{' '}
            <Link to="/cameras" className="underline">
              Cameras page
            </Link>{' '}
            and choose Attach to ticket.
          </p>
        )}
      </CardContent>

      <Dialog open={viewing !== null} onOpenChange={(open) => !open && setViewing(null)}>
        <DialogContent className="max-w-3xl">
          {viewing ? (
            <>
              <DialogHeader>
                <DialogTitle>{MEDIA_KIND_LABEL[viewing.kind]}</DialogTitle>
              </DialogHeader>
              <DialogBody className="space-y-3">
                <MediaViewer
                  media={viewing}
                  src={`${apiBaseUrl}/support-tickets/${ticketId}/media/${viewing.id}/file`}
                />
                <p className="text-sm text-foreground-muted">{mediaDescription(viewing)}</p>
                <CheckoutErrorNote media={viewing} />
                <p className="text-xs text-foreground-subtle">
                  Footage may show customers. Viewing it is recorded in the audit log.
                </p>
              </DialogBody>
              <DialogFooter>
                {can('support:write') ? (
                  <Button
                    variant="ghost"
                    className="mr-auto"
                    loading={detach.isPending}
                    onClick={() => detach.mutate(viewing.id)}
                  >
                    Detach from ticket
                  </Button>
                ) : null}
                <Button variant="secondary" onClick={() => setViewing(null)}>
                  Close
                </Button>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </Card>
  )
}
